import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import PublicNav from "@/components/PublicNav";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import api from "@/lib/api";
import { Ticket, Gift, CheckCircle2 } from "lucide-react";

const COUNTRIES = ["Malaysia","Singapore","France","United Kingdom","United States","Australia","Other"];

export default function Register() {
  const { register } = useAuth();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", password: "", country: "Malaysia" });
  const [code, setCode] = useState("");
  const [showCode, setShowCode] = useState(false);
  const [refCode, setRefCode] = useState("");
  const [showRef, setShowRef] = useState(false);
  const [refValid, setRefValid] = useState(null); // null=unknown, {name} valid, false invalid
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  // Read ?ref= from URL on mount
  useEffect(() => {
    const r = searchParams.get("ref");
    if (r) {
      const upper = r.trim().toUpperCase();
      setRefCode(upper);
      setShowRef(true);
      validateRef(upper);
    }
  }, [searchParams]);

  const validateRef = async (c) => {
    if (!c) { setRefValid(null); return; }
    try {
      const { data } = await api.get(`/referrals/validate/${encodeURIComponent(c)}`);
      setRefValid({ name: data.referrer_name });
    } catch {
      setRefValid(false);
    }
  };

  const onRefBlur = () => {
    if (refCode) validateRef(refCode);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      ...form,
      email: form.email.trim().toLowerCase(),
      referrer_code: refCode.trim() || undefined,
    };
    const r = await register(payload);
    if (!r.ok) { toast.error(r.error); setBusy(false); return; }
    if (code.trim()) {
      try {
        const { data } = await api.post("/codes/redeem", { code: code.trim().toUpperCase() });
        toast.success(`Code accepted — ${data.package} unlocked free`);
        setBusy(false);
        nav("/onboarding");
        return;
      } catch (err) {
        toast.error(err.response?.data?.detail || "Invalid code — proceed to payment");
      }
    }
    setBusy(false);
    toast.success(refCode ? "Account created — share with your friend to claim your free week!" : "Account created");
    nav("/payment");
  };

  return (
    <div className="min-h-screen bg-[#070707] text-white" data-testid="register-page">
      <PublicNav />
      <div className="max-w-md mx-auto px-6 py-12">
        <div className="font-mono-jp text-xs text-[#d7ff00] tracking-[0.3em] uppercase">CREATE ACCOUNT</div>
        <h1 className="mt-3 font-display text-5xl uppercase">Join the <span className="text-[#d7ff00]">crew</span></h1>

        {refValid && (
          <div className="mt-6 bg-[#d7ff00]/10 border border-[#d7ff00] p-4 flex items-start gap-3" data-testid="ref-banner">
            <Gift className="w-5 h-5 text-[#d7ff00] shrink-0 mt-0.5" />
            <div>
              <div className="font-display text-base uppercase tracking-wider text-[#d7ff00]">Invited by {refValid.name}</div>
              <div className="font-mono-jp text-[10px] uppercase tracking-widest text-[#bbb] mt-1">Complete your first program — you both get +1 free week</div>
            </div>
          </div>
        )}

        <form onSubmit={submit} className="mt-8 space-y-4">
          <div>
            <label className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888]">Name</label>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-2 w-full bg-[#0e0e0e] border border-[#222] focus:border-[#d7ff00] outline-none px-4 py-3" data-testid="reg-name" />
          </div>
          <div>
            <label className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888]">Email</label>
            <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mt-2 w-full bg-[#0e0e0e] border border-[#222] focus:border-[#d7ff00] outline-none px-4 py-3" data-testid="reg-email" />
          </div>
          <div>
            <label className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888]">Password</label>
            <input type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="mt-2 w-full bg-[#0e0e0e] border border-[#222] focus:border-[#d7ff00] outline-none px-4 py-3" data-testid="reg-password" />
          </div>
          <div>
            <label className="font-mono-jp text-[10px] uppercase tracking-widest text-[#888]">Country</label>
            <select value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })}
              className="mt-2 w-full bg-[#0e0e0e] border border-[#222] focus:border-[#d7ff00] outline-none px-4 py-3" data-testid="reg-country">
              {COUNTRIES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>

          {showRef ? (
            <div>
              <label className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00] flex items-center gap-1"><Gift className="w-3 h-3" /> Referral code</label>
              <div className="relative">
                <input value={refCode} onChange={(e) => { setRefCode(e.target.value.toUpperCase()); setRefValid(null); }} onBlur={onRefBlur}
                  placeholder="FRIEND-XXXX"
                  className="mt-2 w-full bg-[#0e0e0e] border border-[#d7ff00] focus:border-white outline-none px-4 py-3 font-mono-jp tracking-widest pr-10" data-testid="reg-ref-code" />
                {refValid && <CheckCircle2 className="w-5 h-5 text-[#d7ff00] absolute right-3 top-1/2 -translate-y-1/4" />}
              </div>
              {refValid === false && <div className="mt-1 text-xs text-red-400 font-mono-jp uppercase tracking-widest" data-testid="ref-invalid">Code not found</div>}
              <button type="button" onClick={() => { setShowRef(false); setRefCode(""); setRefValid(null); }} className="mt-1 text-[10px] text-[#888] uppercase tracking-widest font-mono-jp">Remove</button>
            </div>
          ) : (
            <button type="button" onClick={() => setShowRef(true)} className="text-[#d7ff00] text-sm flex items-center gap-1 hover:text-white" data-testid="reg-have-ref">
              <Gift className="w-4 h-4" /> Have a referral code?
            </button>
          )}

          {showCode ? (
            <div>
              <label className="font-mono-jp text-[10px] uppercase tracking-widest text-[#d7ff00] flex items-center gap-1"><Ticket className="w-3 h-3" /> Access code from Coach JP</label>
              <input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="CJP-XXXX-XXXX"
                className="mt-2 w-full bg-[#0e0e0e] border border-[#d7ff00] focus:border-white outline-none px-4 py-3 font-mono-jp tracking-widest" data-testid="reg-code" />
              <button type="button" onClick={() => { setShowCode(false); setCode(""); }} className="mt-1 text-[10px] text-[#888] uppercase tracking-widest font-mono-jp">Remove code</button>
            </div>
          ) : (
            <button type="button" onClick={() => setShowCode(true)} className="text-[#d7ff00] text-sm flex items-center gap-1 hover:text-white" data-testid="reg-have-code">
              <Ticket className="w-4 h-4" /> Have an access code?
            </button>
          )}

          <button type="submit" disabled={busy || refValid === false} className="w-full bg-[#d7ff00] text-black font-display tracking-wider uppercase text-xl py-4 hover:bg-white disabled:opacity-50" data-testid="reg-submit">
            {busy ? "Creating..." : "Create Account"}
          </button>
        </form>
        <p className="mt-6 text-sm text-[#888]">Already a member? <Link to="/login" className="text-[#d7ff00]" data-testid="goto-login">Sign in</Link></p>
      </div>
    </div>
  );
}
